import React, { useState, useMemo, useEffect, useRef } from "react";
import orderService from "../services/orderService";

// ── Design tokens (match Home.jsx) ───────────────────────────────────────────
const BROWN = "#55260c";
const BG    = "#fbf3ef";
const GREEN = "#5F6C37";
const FONT  = "'IBM Plex Sans Hebrew', 'Heebo', sans-serif";

// ── Helpers ───────────────────────────────────────────────────────────────────
function he(obj) {
    if (!obj) return "";
    return typeof obj === "object" ? (obj.he ?? obj.en ?? "") : String(obj ?? "");
}

// ── Shared micro-styles ───────────────────────────────────────────────────────
const inputStyle = {
    width: "100%", padding: "14px 16px", borderRadius: 12,
    border: `2px solid rgba(85,38,12,0.18)`, fontFamily: FONT,
    fontSize: 16, color: BROWN, background: "white",
    outline: "none", boxSizing: "border-box",
};
const primaryBtn = (disabled) => ({
    width: "100%", padding: "15px", borderRadius: 14,
    background: disabled ? "rgba(95,108,55,0.35)" : GREEN,
    border: "none", color: "white", fontFamily: FONT,
    fontSize: 17, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background 0.2s",
});
const secondaryBtn = {
    width: "100%", padding: "12px", borderRadius: 14,
    background: "none", border: `1px solid rgba(85,38,12,0.2)`,
    color: BROWN, fontFamily: FONT, fontSize: 15, cursor: "pointer",
};
const typeCardBtn = {
    width: "100%", padding: "16px 18px", borderRadius: 14,
    background: "white", border: `2px solid rgba(85,38,12,0.1)`,
    display: "flex", alignItems: "center", gap: 14,
    cursor: "pointer", textAlign: "right",
};
const payCardBtn = (dark) => ({
    width: "100%", padding: "16px 18px", borderRadius: 14,
    background: dark ? GREEN : "white",
    border: dark ? "none" : `2px solid rgba(85,38,12,0.12)`,
    display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
});
const qtyCircle = (fill) => ({
    width: 28, height: 28, borderRadius: "50%",
    border: fill ? "none" : `1px solid rgba(85,38,12,0.22)`,
    background: fill ? GREEN : "none",
    color: fill ? "white" : BROWN,
    cursor: "pointer", fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
});

// ── Main component ────────────────────────────────────────────────────────────
export default function OrderModal({
    restaurant,
    dishes,
    dishCategories,
    subCategories,
    dishAddOns,
    dishAddOnCategories,
    onClose,
}) {
    // steps: type | contact | menu | addon | cart | payment | tranzila | success | error
    const [step, setStep]               = useState("type");
    const [orderType, setOrderType]     = useState(null);   // 'table' | 'pickup'
    const [phone, setPhone]             = useState("");
    const [tableNumber, setTableNumber] = useState("");
    const [cart, setCart]               = useState([]);     // [{ key, dish, qty, price, addonsTotal, selectedAddons }]
    const [addonDish, setAddonDish]     = useState(null);   // dish being configured
    const [tempAddons, setTempAddons]   = useState([]);     // working selection in addon picker
    const [menuCatId, setMenuCatId]     = useState(null);
    const [tranzilaUrl, setTranzilaUrl] = useState("");
    const [orderId, setOrderId]         = useState(null);
    const [orderNumber, setOrderNumber] = useState(null);
    const [error, setError]             = useState("");
    const [submitting, setSubmitting]   = useState(false);
    const pollingRef = useRef(null);

    // ── Sorted categories ─────────────────────────────────────────────────────
    const sortedCats = useMemo(
        () => [...dishCategories].sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0)),
        [dishCategories]
    );

    // ── Dishes for the selected menu category ─────────────────────────────────
    const menuDishes = useMemo(() => {
        if (!menuCatId) return [];
        return dishes
            .filter((d) => d.categoryId === menuCatId && d.isAvailable !== false)
            .sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0));
    }, [dishes, menuCatId]);

    // ── Cart totals ───────────────────────────────────────────────────────────
    const cartTotal = useMemo(
        () => cart.reduce((s, i) => s + (i.price + i.addonsTotal) * i.qty, 0),
        [cart]
    );
    const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

    // ── Cleanup polling on unmount ────────────────────────────────────────────
    useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

    // ── Poll order status while Tranzila iframe is open ───────────────────────
    useEffect(() => {
        if (step === "tranzila" && orderId) {
            pollingRef.current = setInterval(async () => {
                try {
                    const res = await orderService.getOrderStatus(orderId);
                    if (res.paymentStatus === "paid" || res.status === "confirmed") {
                        clearInterval(pollingRef.current);
                        setOrderNumber(res.orderNumber);
                        setStep("success");
                    } else if (res.paymentStatus === "failed") {
                        clearInterval(pollingRef.current);
                        setError("התשלום נכשל. נסה שנית.");
                        setStep("error");
                    }
                } catch { /* ignore transient network errors */ }
            }, 2500);
        } else {
            if (pollingRef.current) clearInterval(pollingRef.current);
        }
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [step, orderId]);

    // ── Addon helpers ─────────────────────────────────────────────────────────
    function getAddonGroups(dish) {
        if (!dish) return [];
        const catIds    = dish.addonsCategories || [];
        const addonIds  = dish.addons || [];
        if (catIds.length > 0) {
            return dishAddOnCategories
                .filter((c) => catIds.includes(c._id))
                .map((cat) => ({
                    category: cat,
                    addons: dishAddOns.filter(
                        (a) => a.categoryId === cat._id && a.isAvailable !== false
                    ),
                }))
                .filter((g) => g.addons.length > 0);
        }
        if (addonIds.length > 0) {
            return [{
                category: null,
                addons: dishAddOns.filter((a) => addonIds.includes(a._id) && a.isAvailable !== false),
            }];
        }
        return [];
    }

    // ── Cart operations ───────────────────────────────────────────────────────
    function tryAddDish(dish) {
        const groups = getAddonGroups(dish);
        if (groups.length > 0) {
            setAddonDish(dish);
            setTempAddons([]);
            setStep("addon");
        } else {
            commitAddDish(dish, []);
        }
    }

    function commitAddDish(dish, addons) {
        const base       = parseFloat(dish.price || 0);
        const addonsSum  = addons.reduce((s, a) => s + parseFloat(a.addOnPrice || 0), 0);
        const key        = dish._id + "|" + addons.map((a) => a._id).sort().join(",");
        setCart((prev) => {
            const hit = prev.find((i) => i.key === key);
            if (hit) return prev.map((i) => i.key === key ? { ...i, qty: i.qty + 1 } : i);
            return [...prev, { key, dish, qty: 1, price: base, addonsTotal: addonsSum, selectedAddons: addons }];
        });
    }

    function confirmAddons() {
        commitAddDish(addonDish, tempAddons);
        setAddonDish(null);
        setTempAddons([]);
        setStep("menu");
    }

    function toggleAddon(addon) {
        setTempAddons((prev) =>
            prev.find((a) => a._id === addon._id)
                ? prev.filter((a) => a._id !== addon._id)
                : [...prev, addon]
        );
    }

    function updateQty(key, delta) {
        setCart((prev) =>
            prev.map((i) => i.key === key ? { ...i, qty: i.qty + delta } : i)
                .filter((i) => i.qty > 0)
        );
    }

    // ── Submit order ──────────────────────────────────────────────────────────
    async function handlePlaceOrder(paymentMethod) {
        if (!phone.trim()) { setError("יש להזין מספר טלפון"); return; }
        setError("");
        setSubmitting(true);

        const items = cart.map((i) => ({
            dish:           i.dish,
            quantity:       i.qty,
            selectedAddons: i.selectedAddons,
            itemId:         i.dish._id,
        }));

        try {
            if (paymentMethod === "cash") {
                const res = await orderService.createCashOrder({
                    restaurantId:   restaurant._id,
                    tableNumber:    orderType === "table" ? tableNumber : "",
                    items,
                    total:          cartTotal,
                    firstName:      "אורח",
                    lastName:       "",
                    userPhone:      phone,
                    status:         "pending",
                    paymentMethod:  "cash",
                    paymentStatus:  "pending",
                    deliveryMethod: orderType === "table" ? "dine_in" : "pickup",
                    deliveryStatus: "pending",
                });
                setOrderNumber(res.orderNumber);
                setOrderId(res.orderId);
                setStep("success");
                setCart([]);
            } else {
                const res = await orderService.initiateCardPayment({
                    restaurantId:   restaurant._id,
                    tableNumber:    orderType === "table" ? tableNumber : "",
                    items,
                    total:          cartTotal,
                    firstName:      "אורח",
                    lastName:       "",
                    userPhone:      phone,
                    deliveryMethod: orderType === "table" ? "dine_in" : "pickup",
                });
                setOrderId(res.orderId);
                setOrderNumber(res.orderNumber);
                setTranzilaUrl(res.tranzilaUrl);
                setStep("tranzila");
                setCart([]);
            }
        } catch (err) {
            setError(err?.response?.data?.message || "שגיאה בשליחת ההזמנה. נסה שנית.");
            setStep("error");
        } finally {
            setSubmitting(false);
        }
    }

    // ── Back map ──────────────────────────────────────────────────────────────
    const backMap = {
        contact: "type",
        menu:    "contact",
        cart:    "menu",
        payment: "cart",
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const sheetStyle = {
        position: "fixed", bottom: 0, left: "50%",
        transform: "translateX(-50%)",
        width: "100%", maxWidth: 440, zIndex: 200,
        background: BG,
        borderRadius: "20px 20px 0 0",
        maxHeight: ["menu", "addon"].includes(step) ? "92vh" : "86vh",
        overflowY: "auto",
        boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: "fixed", inset: 0, zIndex: 199,
                    background: "rgba(0,0,0,0.5)",
                    backdropFilter: "blur(3px)",
                }}
            />

            <div style={sheetStyle} dir="rtl">
                {/* Sticky header */}
                <div style={{
                    position: "sticky", top: 0, background: BG,
                    padding: "14px 20px 8px", zIndex: 1,
                    borderRadius: "20px 20px 0 0",
                    borderBottom: "1px solid rgba(85,38,12,0.06)",
                }}>
                    <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(85,38,12,0.14)", margin: "0 auto 10px" }} />
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        {backMap[step] ? (
                            <button type="button"
                                onClick={() => setStep(backMap[step])}
                                style={{ background: "none", border: "none", color: BROWN, fontFamily: FONT, fontSize: 14, cursor: "pointer", padding: 0 }}>
                                ← חזור
                            </button>
                        ) : <span />}
                        <button type="button" onClick={onClose}
                            style={{ background: "none", border: "none", color: BROWN, opacity: 0.45, fontSize: 22, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>
                            ✕
                        </button>
                    </div>
                </div>

                <div style={{ padding: "16px 20px 40px" }}>

                    {/* ══ STEP: type ════════════════════════════════════════════ */}
                    {step === "type" && (
                        <>
                            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BROWN, margin: "0 0 6px" }}>סוג ההזמנה</h2>
                            <p style={{ fontFamily: FONT, fontSize: 14, color: BROWN, opacity: 0.55, margin: "0 0 22px" }}>כיצד תרצה לקבל את ההזמנה?</p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                <button type="button"
                                    onClick={() => { setOrderType("table"); setStep("contact"); }}
                                    style={typeCardBtn}>
                                    <div>
                                        <p style={{ fontFamily: FONT, fontSize: 17, fontWeight: 700, color: BROWN, margin: 0 }}>לשבת</p>
                                        <p style={{ fontFamily: FONT, fontSize: 12, color: BROWN, opacity: 0.5, margin: 0 }}>ההזמנה תגיע ישירות לשולחן</p>
                                    </div>
                                </button>
                                <button type="button"
                                    onClick={() => { setOrderType("pickup"); setStep("contact"); }}
                                    style={typeCardBtn}>
                                    <div>
                                        <p style={{ fontFamily: FONT, fontSize: 17, fontWeight: 700, color: BROWN, margin: 0 }}>לקחת</p>
                                        <p style={{ fontFamily: FONT, fontSize: 12, color: BROWN, opacity: 0.5, margin: 0 }}>לקחת מהדלפק</p>
                                    </div>
                                </button>
                            </div>
                        </>
                    )}

                    {/* ══ STEP: contact ═════════════════════════════════════════ */}
                    {step === "contact" && (
                        <>
                            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BROWN, margin: "0 0 20px" }}>פרטי הזמנה</h2>
                            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                <div>
                                    <label style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.7, display: "block", marginBottom: 6 }}>מספר טלפון *</label>
                                    <input type="tel" inputMode="numeric" placeholder="050-0000000"
                                        value={phone}
                                        onChange={(e) => { setPhone(e.target.value); setError(""); }}
                                        style={inputStyle} />
                                </div>
                                {orderType === "table" && (
                                    <div>
                                        <label style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.7, display: "block", marginBottom: 6 }}>מספר שולחן *</label>
                                        <input type="number" inputMode="numeric" placeholder="לדוגמה: 5"
                                            value={tableNumber}
                                            onChange={(e) => { setTableNumber(e.target.value); setError(""); }}
                                            style={{ ...inputStyle, textAlign: "center", fontSize: 22 }} />
                                    </div>
                                )}
                            </div>
                            {error && <p style={{ fontFamily: FONT, color: "#c0392b", fontSize: 13, marginTop: 8, textAlign: "center" }}>{error}</p>}
                            <button type="button"
                                disabled={!phone.trim() || (orderType === "table" && !tableNumber.trim())}
                                onClick={() => {
                                    if (!phone.trim()) { setError("יש להזין מספר טלפון"); return; }
                                    if (orderType === "table" && !tableNumber.trim()) { setError("יש להזין מספר שולחן"); return; }
                                    if (!menuCatId && sortedCats.length > 0) setMenuCatId(sortedCats[0]._id);
                                    setStep("menu");
                                }}
                                style={{ ...primaryBtn(!phone.trim() || (orderType === "table" && !tableNumber.trim())), marginTop: 22 }}>
                                בחר מנות מהתפריט ←
                            </button>
                        </>
                    )}

                    {/* ══ STEP: menu ════════════════════════════════════════════ */}
                    {step === "menu" && (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: BROWN, margin: 0 }}>בחר מנות</h2>
                                {cartCount > 0 && (
                                    <button type="button" onClick={() => setStep("cart")}
                                        style={{ background: GREEN, border: "none", borderRadius: 20, color: "white", fontFamily: FONT, fontSize: 14, fontWeight: 700, padding: "8px 16px", cursor: "pointer" }}>
                                        🛒 {cartCount} | ₪{cartTotal.toFixed(0)}
                                    </button>
                                )}
                            </div>

                            {/* Category tabs */}
                            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 10, marginBottom: 14, scrollbarWidth: "none", msOverflowStyle: "none" }}>
                                {sortedCats.map((cat) => (
                                    <button key={cat._id} type="button"
                                        onClick={() => setMenuCatId(cat._id)}
                                        style={{
                                            flexShrink: 0, padding: "7px 14px", borderRadius: 20,
                                            border: "none", cursor: "pointer",
                                            fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                            background: menuCatId === cat._id ? GREEN : "rgba(85,38,12,0.08)",
                                            color: menuCatId === cat._id ? "white" : BROWN,
                                            whiteSpace: "nowrap",
                                            transition: "background 0.15s",
                                        }}>
                                        {he(cat.categoryName)}
                                    </button>
                                ))}
                            </div>

                            {/* Dishes */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {menuDishes.length === 0 && (
                                    <p style={{ fontFamily: FONT, color: BROWN, opacity: 0.35, textAlign: "center", padding: "24px 0" }}>בחר קטגוריה מלמעלה</p>
                                )}
                                {menuDishes.map((dish) => {
                                    const inCart     = cart.filter((i) => i.dish._id === dish._id).reduce((s, i) => s + i.qty, 0);
                                    const hasAddons  = (dish.addons?.length > 0 || dish.addonsCategories?.length > 0);
                                    return (
                                        <div key={dish._id} style={{
                                            background: "white", borderRadius: 14,
                                            padding: "12px 14px",
                                            display: "flex", alignItems: "center",
                                            justifyContent: "space-between", gap: 10,
                                        }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: BROWN, margin: 0 }}>{he(dish.dishName)}</p>
                                                {dish.price && (
                                                    <p style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.6, margin: "2px 0 0" }}>₪{dish.price}</p>
                                                )}
                                                {hasAddons && (
                                                    <p style={{ fontFamily: FONT, fontSize: 11, color: GREEN, margin: "2px 0 0" }}>ניתן להוסיף תוספות</p>
                                                )}
                                            </div>
                                            {dish.dishImage && (
                                                <img src={dish.dishImage} alt={he(dish.dishName)}
                                                    loading="lazy" decoding="async"
                                                    style={{ width: 58, height: 58, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                                            )}
                                            <button type="button" onClick={() => tryAddDish(dish)}
                                                style={{
                                                    ...qtyCircle(inCart > 0),
                                                    width: 34, height: 34, fontSize: inCart > 0 ? 14 : 22, fontWeight: 700,
                                                }}>
                                                {inCart > 0 ? inCart : "+"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {cartCount > 0 && (
                                <button type="button" onClick={() => setStep("cart")}
                                    style={{ ...primaryBtn(false), marginTop: 18 }}>
                                    לסל ({cartCount} פריטים) — ₪{cartTotal.toFixed(2)} ←
                                </button>
                            )}
                        </>
                    )}

                    {/* ══ STEP: addon picker ════════════════════════════════════ */}
                    {step === "addon" && addonDish && (
                        <>
                            <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: BROWN, margin: "0 0 4px" }}>{he(addonDish.dishName)}</h2>
                            <p style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.55, margin: "0 0 18px" }}>בחר תוספות (אופציונלי)</p>

                            {getAddonGroups(addonDish).map((group, gi) => (
                                <div key={gi} style={{ marginBottom: 18 }}>
                                    {group.category && (
                                        <p style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: BROWN, margin: "0 0 10px", opacity: 0.8 }}>
                                            {group.category.categoryName}
                                        </p>
                                    )}
                                    {group.addons.map((addon) => {
                                        const selected = tempAddons.some((a) => a._id === addon._id);
                                        return (
                                            <button key={addon._id} type="button"
                                                onClick={() => toggleAddon(addon)}
                                                style={{
                                                    width: "100%",
                                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                                    padding: "11px 14px", borderRadius: 10, marginBottom: 7,
                                                    background: selected ? "rgba(95,108,55,0.1)" : "white",
                                                    border: `2px solid ${selected ? GREEN : "rgba(85,38,12,0.08)"}`,
                                                    cursor: "pointer", transition: "all 0.15s",
                                                }}>
                                                <span style={{ fontFamily: FONT, fontSize: 14, color: BROWN }}>{he(addon.addOnName)}</span>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    {addon.addOnPrice && parseFloat(addon.addOnPrice) > 0 && (
                                                        <span style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.55 }}>+₪{addon.addOnPrice}</span>
                                                    )}
                                                    <div style={{
                                                        width: 22, height: 22, borderRadius: "50%",
                                                        border: `2px solid ${selected ? GREEN : "rgba(85,38,12,0.18)"}`,
                                                        background: selected ? GREEN : "transparent",
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}>
                                                        {selected && <span style={{ color: "white", fontSize: 13, fontWeight: 700 }}>✓</span>}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}

                            <button type="button" onClick={confirmAddons}
                                style={{ ...primaryBtn(false), marginTop: 6 }}>
                                הוסף לסל{tempAddons.length > 0 ? ` (+ ${tempAddons.length} תוספות)` : ""}
                            </button>
                            <button type="button" onClick={() => { setAddonDish(null); setStep("menu"); }}
                                style={{ ...secondaryBtn, marginTop: 8 }}>
                                ביטול
                            </button>
                        </>
                    )}

                    {/* ══ STEP: cart ════════════════════════════════════════════ */}
                    {step === "cart" && (
                        <>
                            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BROWN, margin: "0 0 16px" }}>סל הקניות</h2>
                            {cart.length === 0 ? (
                                <p style={{ fontFamily: FONT, color: BROWN, opacity: 0.4, textAlign: "center", padding: "36px 0" }}>הסל ריק</p>
                            ) : (
                                <>
                                    {cart.map((item) => (
                                        <div key={item.key} style={{ padding: "11px 0", borderBottom: "1px solid rgba(85,38,12,0.07)" }}>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ fontFamily: FONT, fontSize: 15, color: BROWN, margin: 0 }}>{he(item.dish.dishName)}</p>
                                                    {item.selectedAddons.length > 0 && (
                                                        <p style={{ fontFamily: FONT, fontSize: 11, color: BROWN, opacity: 0.5, margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            + {item.selectedAddons.map((a) => he(a.addOnName)).join(", ")}
                                                        </p>
                                                    )}
                                                    <p style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.6, margin: "2px 0 0" }}>
                                                        ₪{((item.price + item.addonsTotal) * item.qty).toFixed(2)}
                                                    </p>
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <button type="button" onClick={() => updateQty(item.key, -1)} style={qtyCircle(false)}>−</button>
                                                    <span style={{ fontFamily: FONT, fontSize: 16, color: BROWN, minWidth: 22, textAlign: "center" }}>{item.qty}</span>
                                                    <button type="button" onClick={() => updateQty(item.key, 1)} style={qtyCircle(true)}>+</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 0 0", fontFamily: FONT, fontSize: 17, fontWeight: 700, color: BROWN }}>
                                        <span>סה"כ</span>
                                        <span>₪{cartTotal.toFixed(2)}</span>
                                    </div>
                                    <button type="button" onClick={() => setStep("payment")} style={{ ...primaryBtn(false), marginTop: 20 }}>
                                        המשך לתשלום ←
                                    </button>
                                    <button type="button" onClick={() => setStep("menu")} style={{ ...secondaryBtn, marginTop: 8 }}>
                                        + הוסף עוד מנות
                                    </button>
                                </>
                            )}
                        </>
                    )}

                    {/* ══ STEP: payment ════════════════════════════════════════ */}
                    {step === "payment" && (
                        <>
                            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BROWN, margin: "0 0 6px" }}>אמצעי תשלום</h2>
                            <p style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.55, margin: "0 0 20px" }}>
                                {orderType === "table" ? `שולחן ${tableNumber}` : "איסוף עצמי"} · {phone} · סה"כ ₪{cartTotal.toFixed(2)}
                            </p>
                            {error && <p style={{ fontFamily: FONT, color: "#c0392b", fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</p>}

                            {/* Cash — table only */}
                            {orderType === "table" && (
                                <button type="button" disabled={submitting} onClick={() => handlePlaceOrder("cash")}
                                    style={payCardBtn(false)}>
                                    <div style={{ textAlign: "right" }}>
                                        <p style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: BROWN, margin: 0 }}>מזומן</p>
                                        <p style={{ fontFamily: FONT, fontSize: 12, color: BROWN, opacity: 0.5, margin: 0 }}>ההזמנה נשלחת לאישור הצוות</p>
                                    </div>
                                </button>
                            )}

                            {/* Credit card */}
                            <button type="button" disabled={submitting} onClick={() => handlePlaceOrder("credit_card")}
                                style={{ ...payCardBtn(true), marginTop: orderType === "table" ? 10 : 0 }}>
                                <div style={{ textAlign: "right" }}>
                                    <p style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: "white", margin: 0 }}>כרטיס אשראי</p>
                                    <p style={{ fontFamily: FONT, fontSize: 12, color: "rgba(255,255,255,0.65)", margin: 0 }}>תשלום מאובטח </p>
                                </div>
                            </button>

                            {submitting && (
                                <p style={{ fontFamily: FONT, fontSize: 14, color: BROWN, opacity: 0.55, textAlign: "center", marginTop: 14 }}>שולח הזמנה...</p>
                            )}
                        </>
                    )}

                    {/* ══ STEP: tranzila iframe ════════════════════════════════ */}
                    {step === "tranzila" && (
                        <>
                            <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: BROWN, margin: "0 0 6px" }}>תשלום מאובטח</h2>
                            <p style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.5, margin: "0 0 14px" }}>
                                ממתין לאישור תשלום — אל תסגור את החלון
                            </p>
                            <iframe
                                src={tranzilaUrl}
                                title="תשלום מאובטח"
                                style={{ width: "100%", height: 500, border: "none", borderRadius: 14, display: "block" }}
                                allow="payment"
                            />
                        </>
                    )}

                    {/* ══ STEP: success ════════════════════════════════════════ */}
                    {step === "success" && (
                        <div style={{ textAlign: "center", padding: "20px 0 8px" }}>
                            <div style={{ fontSize: 58, marginBottom: 14 }}>✅</div>
                            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BROWN, margin: "0 0 8px" }}>ההזמנה התקבלה!</h2>
                            {orderNumber && (
                                <p style={{ fontFamily: FONT, fontSize: 15, color: BROWN, opacity: 0.7, margin: "0 0 6px" }}>
                                    מספר הזמנה: <strong>#{orderNumber}</strong>
                                </p>
                            )}
                            <p style={{ fontFamily: FONT, fontSize: 13, color: BROWN, opacity: 0.5, margin: "0 0 28px" }}>
                                {orderType === "table"
                                    ? `שולחן ${tableNumber} — הצוות שלנו יטפל בהזמנה בקרוב`
                                    : "ניתן לאסוף את ההזמנה מהדלפק"}
                            </p>
                            <button type="button" onClick={onClose} style={{ ...primaryBtn(false), maxWidth: 200, margin: "0 auto" }}>
                                סגור
                            </button>
                        </div>
                    )}

                    {/* ══ STEP: error ══════════════════════════════════════════ */}
                    {step === "error" && (
                        <div style={{ textAlign: "center", padding: "20px 0 8px" }}>
                            <div style={{ fontSize: 58, marginBottom: 14 }}>❌</div>
                            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BROWN, margin: "0 0 8px" }}>שגיאה</h2>
                            <p style={{ fontFamily: FONT, fontSize: 14, color: BROWN, opacity: 0.55, margin: "0 0 28px" }}>
                                {error || "אירעה שגיאה. נסה שנית."}
                            </p>
                            <button type="button" onClick={() => { setError(""); setStep("payment"); }}
                                style={{ ...primaryBtn(false), maxWidth: 200, margin: "0 auto" }}>
                                נסה שנית
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}
